import { useMutation, useQueryClient } from '@tanstack/react-query';
import { planPaymentRequestApi } from '@src/apis/admin';
import type { ApplyPaymentRequestModel } from '@uniquegood/realworld-admin-interface';

/**
 * 결제 요청을 완료 처리하고 요금제를 적용/연장하는 mutation
 *
 * @description
 * - 진행 중인 결제 요청을 완료 처리하고 요금제를 적용하거나 연장합니다.
 * - PlanPaymentRequestApi.applyPlanByPaymentRequest를 사용하여 실제 API를 호출합니다.
 * - 성공 시 관련 쿼리들을 무효화하여 데이터를 갱신합니다.
 */
export const useApplyPlanByPaymentRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['applyPlanByPaymentRequest'],
        mutationFn: async ({
            channelId,
            paymentRequestId,
            paymentAt,
        }: {
            channelId: string;
            paymentRequestId: string;
            paymentAt?: string;
        }) => {
            const requestBody: ApplyPaymentRequestModel = {
                paymentAt: paymentAt ? new Date(paymentAt).toISOString() : undefined,
            };

            const { data } = await planPaymentRequestApi.applyPlanByPaymentRequest(
                channelId,
                paymentRequestId,
                "1.0", // x-rwd-api-version은 default value 사용
                requestBody
            );

            return { data, channelId };
        },
        onSuccess: (response) => {
            // 관련 쿼리 무효화하여 재조회 유도
            queryClient.invalidateQueries({ queryKey: ['current-payment-request', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['plan-history', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channel-detail', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channels'] });
        },
    });
};

